import React from 'react';
import Modal from '../Modal';
import { Exportateur } from '../../types/weighing';
import { ExclamationTriangleIcon, TrashIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

interface DeleteExporterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  exporter: Exportateur;
}

const DeleteExporterModal: React.FC<DeleteExporterModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  exporter 
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Supprimer l'exportateur" size="md">
      <div className="space-y-6">
        {/* Icône d'avertissement */}
        <div className="flex justify-center">
          <div className="p-3 bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
        </div>

        {/* Message d'avertissement */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Êtes-vous sûr de vouloir supprimer cet exportateur ?
          </h3>
          <p className="text-sm text-gray-600">
            Cette action est irréversible et supprimera définitivement l'exportateur et toutes ses données associées.
          </p>
        </div>

        {/* Résumé de l'exportateur à supprimer */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <GlobeAltIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{exporter.nom}</h4>
              <div className="text-sm text-gray-600 space-y-1 mt-1">
                {exporter.type && (
                  <div>Type: {exporter.type}</div>
                )}
                {exporter.pays && (
                  <div>Pays: {exporter.pays}</div>
                )}
                {exporter.zone && (
                  <div>Zone: {exporter.zone}</div>
                )}
                {exporter.telephone && (
                  <div>Téléphone: {exporter.telephone}</div>
                )}
                {exporter.email && (
                  <div>Email: {exporter.email}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Avertissement supplémentaire */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Attention :</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li>Toutes les transactions associées à cet exportateur seront affectées</li>
                <li>Cette action ne peut pas être annulée</li>
                <li>Assurez-vous de sauvegarder toutes les informations importantes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <TrashIcon className="h-4 w-4" />
            <span>Supprimer définitivement</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteExporterModal;
